import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { useAppointments, Appointment } from '../../crm-appointments/useData';
import { appointmentsCalendarPath } from '../../crm-appointments/paths';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { getUpcomingRangeFromTimeRange } from '../contexts/DashboardFiltersContext';
import {
  formatDateLocalized,
  formatLocaleDateTime,
  formatTime as formatAppTime,
} from '../../../config/appConfig';
import {
  dateChipClass,
  emptyStateIconClass,
  listRowClassCenter,
  skeletonPulseClass,
  viewAllLinkClass,
  widgetCardClass,
  widgetTitleClass,
} from '../themeHelpers';
import type { TimeRange } from './TimeRangeSelector';
import type { DashboardScope } from '../contexts/DashboardFiltersContext';

export interface UpcomingAppointmentsProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  timeRange?: TimeRange;
  scope?: DashboardScope;
}

const UpcomingAppointments: React.FC<UpcomingAppointmentsProps> = ({
  limit: limitProp = 5,
  embedded,
  settings,
  timeRange = 'month',
  scope = 'myWork',
}) => {
  const limit = typeof settings?.limit === 'number' ? settings.limit : limitProp;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { appointments, isLoading } = useAppointments();
  const { colors } = useTheme();
  const cardClass = widgetCardClass(colors, embedded);
  const listClass = embedded ? 'flex-1 min-h-0 overflow-y-auto space-y-1' : 'space-y-1';

  const { startDate, endDate } = useMemo(() => getUpcomingRangeFromTimeRange(timeRange), [timeRange]);
  const upcoming = (appointments || [])
    .filter((apt: Appointment) => {
      if (!apt.clientAppScheduleDateTime) return false;
      const aptDate = new Date(apt.clientAppScheduleDateTime);
      if (aptDate < startDate) return false;
      if (aptDate > endDate) return false;
      if (scope === 'myWork' && user?.id != null && apt.assignedTo !== user.id) return false;
      return true;
    })
    .sort((a: Appointment, b: Appointment) => {
      const dateA = new Date(a.clientAppScheduleDateTime || 0).getTime();
      const dateB = new Date(b.clientAppScheduleDateTime || 0).getTime();
      return dateA - dateB;
    })
    .slice(0, limit);

  const formatAppointmentWhen = (dateString?: string): { date: string; time: string; full: string } => {
    if (!dateString) {
      return { date: 'N/A', time: 'N/A', full: 'N/A' };
    }
    const date = new Date(dateString);
    return {
      date: formatDateLocalized(date, { month: 'short', day: 'numeric' }),
      time: formatAppTime(date),
      full: formatLocaleDateTime(date),
    };
  };

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className={`${widgetTitleClass(colors)} mb-3`}>Upcoming Appointments</h2>
        <div className="space-y-2 sm:space-y-3">
          {[1, 2, 3].map((item) => (
            <div key={item} className={`flex items-center space-x-3 sm:space-x-4 pb-2 sm:pb-3 ${colors.border} border-b last:border-0`}>
              <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-lg ${skeletonPulseClass(colors)} flex-shrink-0`} />
              <div className="flex-1 min-w-0">
                <div className={`h-3 sm:h-4 ${skeletonPulseClass(colors, 'w-3/4 mb-1 sm:mb-2')}`} />
                <div className={`h-2.5 sm:h-3 ${skeletonPulseClass(colors, 'w-1/2')}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h2 className={widgetTitleClass(colors)}>Upcoming Appointments</h2>
        <button
          onClick={() => navigate(appointmentsCalendarPath())}
          className={viewAllLinkClass(colors)}
        >
          View All
        </button>
      </div>
      <div className={listClass}>
        {upcoming.length > 0 ? (
          upcoming.map((apt: Appointment) => {
            const dateTime = formatAppointmentWhen(apt.clientAppScheduleDateTime);
            const dateParts = dateTime.date.split(' ');
            return (
              <div
                key={apt.clientAppId}
                onClick={() => navigate(`/appointments/${apt.clientAppId}`)}
                className={listRowClassCenter(colors, `${colors.borderLight} border-b last:border-0`)}
              >
                <div className="flex-shrink-0">
                  <div className={dateChipClass(colors)}>
                    {dateParts.length >= 2 ? (
                      <>
                        <span className="text-[10px] font-semibold sm:text-xs">{dateParts[0]}</span>
                        <span className="text-xs font-bold sm:text-sm">{dateParts[1]}</span>
                      </>
                    ) : (
                      <span className="text-[10px] font-semibold sm:text-xs">N/A</span>
                    )}
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`truncate text-xs font-medium sm:text-sm ${colors.text}`}>
                    {apt.clientAppName || (apt.client?.clientName ? (apt.client.clientName.length > 20 ? apt.client.clientName.substring(0, 20) + '...' : apt.client.clientName) : 'Untitled Appointment')}
                  </p>
                  <div className="mt-0.5 flex items-center gap-1.5 sm:mt-1 sm:gap-2">
                    <Clock className={`h-3 w-3 shrink-0 sm:h-4 sm:w-4 ${colors.textMuted}`} aria-hidden />
                    <p className={`truncate text-[10px] sm:text-xs ${colors.textMuted}`}>{dateTime.full}</p>
                  </div>
                  {apt.client?.clientName && (
                    <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-0.5 sm:mt-1 truncate`}>{apt.client.clientName.length > 20 ? apt.client.clientName.substring(0, 20) + '...' : apt.client.clientName}</p>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className={`text-center py-8 ${colors.textMuted}`}>
            <svg className={emptyStateIconClass(colors)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-sm sm:text-base">No upcoming appointments</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpcomingAppointments;
